import { useAuth } from "../context/AuthContext";
import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  ScrollText, Search, RefreshCw, Calendar,
  PlusCircle, ThumbsUp, ThumbsDown, XCircle, LogOut,
  IndianRupee, ClipboardCheck, Star, UserX,
  MessageSquare, Send, Mail,
} from "lucide-react";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

// Same type → icon/color convention as the bell dropdown (AdminLayout.jsx),
// duplicated locally rather than shared — matches this codebase's existing
// pattern of per-page style constants (PLATFORM_STYLES, STATUS_COLORS, etc).
const TYPE_STYLES = {
  booking_new:          { Icon: PlusCircle,     color: "text-green-600 bg-green-50" },
  booking_approved:     { Icon: ThumbsUp,       color: "text-green-600 bg-green-50" },
  booking_rejected:     { Icon: ThumbsDown,     color: "text-red-600 bg-red-50" },
  booking_cancelled:    { Icon: XCircle,        color: "text-red-600 bg-red-50" },
  booking_no_show:      { Icon: UserX,          color: "text-orange-600 bg-orange-50" },
  booking_completed:    { Icon: LogOut,         color: "text-purple-600 bg-purple-50" },
  payment_recorded:     { Icon: IndianRupee,    color: "text-amber-600 bg-amber-50" },
  guest_form_submitted: { Icon: ClipboardCheck, color: "text-blue-600 bg-blue-50" },
  review_submitted:     { Icon: Star,           color: "text-yellow-600 bg-yellow-50" },
};
const DEFAULT_STYLE = { Icon: ScrollText, color: "text-gray-600 bg-gray-50" };

const typeLabel = t => t.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

export default function LogsPage() {
  const [tab, setTab] = useState("activity");
  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-4">
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
        <button onClick={() => setTab("activity")}
          className={`tap-44 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === "activity" ? "bg-white shadow-sm text-gray-900" : "text-gray-500"}`}>
          Activity
        </button>
        <button onClick={() => setTab("messages")}
          className={`tap-44 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === "messages" ? "bg-white shadow-sm text-gray-900" : "text-gray-500"}`}>
          Messages
        </button>
      </div>
      {tab === "activity" ? <ActivityTab /> : <MessagesTab />}
    </div>
  );
}

function ActivityTab() {
  const { authHeaders } = useAuth();
  const [logs, setLogs]         = useState([]);
  const [types, setTypes]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [page, setPage]         = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal]       = useState(0);

  const [search, setSearch]         = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("All");
  const [from, setFrom]             = useState("");
  const [to, setTo]                 = useState("");

  // Debounce the search box so it doesn't hit the server on every keystroke,
  // and jump back to page 1 whenever the query settles — same pattern as
  // BookingsPage/CustomersPage.
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  // Guards against out-of-order responses: an earlier, broader request can
  // resolve after a later, narrower one and clobber it. Only the most
  // recently *issued* request is allowed to write to state.
  const requestIdRef = useRef(0);
  const load = useCallback(async () => {
    const reqId = ++requestIdRef.current;
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (debouncedSearch)       params.set("search", debouncedSearch);
      if (typeFilter !== "All") params.set("type", typeFilter);
      if (from) params.set("from", from);
      if (to)   params.set("to", to);

      const res  = await fetch(`${API}/logs?${params}`, { headers: authHeaders() });
      const json = await res.json();
      if (reqId === requestIdRef.current) {
        setLogs(json.data || []);
        setTotalPages(json.totalPages || 1);
        setTotal(json.total || 0);
        setTypes(json.types || []);
      }
    } catch {
      if (reqId === requestIdRef.current) setLogs([]);
    }
    if (reqId === requestIdRef.current) setLoading(false);
  }, [page, debouncedSearch, typeFilter, from, to]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ScrollText size={22} className="text-gray-400" /> Logs
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Every event recorded across the PMS — {total} total</p>
        </div>
        <button onClick={load}
          className="tap-44 p-2 hover:bg-gray-100 rounded-xl transition-colors">
          <RefreshCw size={16} className={`text-gray-500 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 bg-white p-4 rounded-2xl border border-gray-100 shadow-sm">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search title or message…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-xl
              focus:outline-none focus:ring-2 focus:ring-amber-400"
          />
        </div>
        <select
          value={typeFilter}
          onChange={e => { setTypeFilter(e.target.value); setPage(1); }}
          className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
        >
          <option value="All">All Event Types</option>
          {types.map(t => <option key={t} value={t}>{typeLabel(t)}</option>)}
        </select>
        <div className="flex items-center gap-2 flex-wrap">
          <Calendar size={15} className="text-gray-400" />
          <input type="date" value={from} onChange={e => { setFrom(e.target.value); setPage(1); }}
            className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
          <span className="text-sm text-gray-400">to</span>
          <input type="date" value={to} onChange={e => { setTo(e.target.value); setPage(1); }}
            className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
        </div>
      </div>

      {/* Log table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="hidden md:block overflow-auto max-h-[65vh]">
          <table className="text-xs">
            <thead className="sticky top-0 z-10 bg-white">
              <tr className="border-b border-gray-50">
                {["Event", "Title & Message", "Booking", "When"].map(h => (
                  <th key={h} className="text-left text-[10.5px] font-semibold text-gray-400
                    uppercase tracking-wider px-3 py-2 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                <tr><td colSpan={4} className="text-center py-12 text-gray-400">
                  <RefreshCw size={20} className="animate-spin mx-auto mb-2" /> Loading…
                </td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan={4} className="text-center py-10 text-gray-400 text-sm">No events match this filter</td></tr>
              ) : logs.map(log => {
                const style = TYPE_STYLES[log.type] || DEFAULT_STYLE;
                const { Icon } = style;
                return (
                  <tr key={log._id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1.5 text-[10.5px] font-semibold
                        px-2 py-0.5 rounded-full ${style.color}`}>
                        <Icon size={11} /> {typeLabel(log.type)}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <p className="font-medium text-gray-900">{log.title}</p>
                      {log.message && <p className="text-[10.5px] text-gray-500 mt-0.5">{log.message}</p>}
                    </td>
                    <td className="px-3 py-2 font-mono text-gray-500 whitespace-nowrap">
                      {log.booking?.bookingRef || "—"}
                    </td>
                    <td className="px-3 py-2 text-gray-500 whitespace-nowrap">
                      {new Date(log.createdAt).toLocaleString("en-IN", {
                        day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
                      })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="md:hidden divide-y divide-gray-100 max-h-[75vh] overflow-auto">
          {loading ? (
            <div className="text-center py-12 text-gray-400">
              <RefreshCw size={20} className="animate-spin mx-auto mb-2" /> Loading…
            </div>
          ) : logs.length === 0 ? (
            <p className="text-center py-10 text-gray-400 text-sm">No events match this filter</p>
          ) : (
            logs.map((log) => {
              const style = TYPE_STYLES[log.type] || DEFAULT_STYLE;
              const { Icon } = style;
              return (
                <div key={log._id} className="p-3.5 space-y-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className={`inline-flex items-center gap-1.5 text-[10.5px] font-semibold px-2 py-0.5 rounded-full ${style.color}`}>
                      <Icon size={11} /> {typeLabel(log.type)}
                    </span>
                    <span className="text-[10.5px] text-gray-400 whitespace-nowrap">
                      {new Date(log.createdAt).toLocaleString("en-IN", {
                        day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
                      })}
                    </span>
                  </div>
                  <p className="font-medium text-gray-900 text-sm">{log.title}</p>
                  {log.message && <p className="text-xs text-gray-500">{log.message}</p>}
                  {log.booking?.bookingRef && (
                    <p className="text-[10.5px] font-mono text-gray-400">{log.booking.bookingRef}</p>
                  )}
                </div>
              );
            })
          )}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="text-sm px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">
              Previous
            </button>
            <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="text-sm px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const CHANNEL_STYLE = {
  whatsapp: { Icon: MessageSquare, color: "text-green-600 bg-green-50" },
  telegram: { Icon: Send,          color: "text-blue-600 bg-blue-50" },
  email:    { Icon: Mail,          color: "text-purple-600 bg-purple-50" },
};
const STATUS_STYLE = {
  // "sent" only means the provider accepted it — deliberately not the same
  // green as "delivered", which the WhatsApp webhook confirms later.
  sent:      "text-sky-700 bg-sky-50 border-sky-200",
  delivered: "text-green-700 bg-green-50 border-green-200",
  read:      "text-emerald-800 bg-emerald-50 border-emerald-300",
  failed:    "text-red-700 bg-red-50 border-red-200",
  skipped: "text-gray-500 bg-gray-100 border-gray-200",
};

function MessagesTab() {
  const { authHeaders } = useAuth();
  const [logs, setLogs]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage]     = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal]   = useState(0);
  const [channel, setChannel] = useState("All");
  const [status, setStatus]   = useState("All");

  const requestIdRef = useRef(0);
  const load = useCallback(async () => {
    const reqId = ++requestIdRef.current;
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (channel !== "All") params.set("channel", channel);
      if (status !== "All")  params.set("status", status);
      const res  = await fetch(`${API}/messages/log?${params}`, { headers: authHeaders() });
      const json = await res.json();
      if (reqId === requestIdRef.current) {
        setLogs(json.data || []);
        setTotalPages(json.totalPages || 1);
        setTotal(json.total || 0);
      }
    } catch {
      if (reqId === requestIdRef.current) setLogs([]);
    }
    if (reqId === requestIdRef.current) setLoading(false);
  }, [page, channel, status]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <MessageSquare size={22} className="text-gray-400" /> Messages
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Every WhatsApp/Telegram/Email send — {total} total</p>
          <p className="text-[11px] text-gray-400 mt-0.5">
            WhatsApp “sent” only means WhatsApp accepted it. It changes to delivered, read or failed
            once the delivery webhook is connected (Settings → Notifications → WhatsApp).
          </p>
        </div>
        <button onClick={load} className="tap-44 p-2 hover:bg-gray-100 rounded-xl transition-colors">
          <RefreshCw size={16} className={`text-gray-500 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      <div className="flex flex-wrap gap-3 bg-white p-4 rounded-2xl border border-gray-100 shadow-sm">
        <select value={channel} onChange={e => { setChannel(e.target.value); setPage(1); }}
          className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white">
          <option value="All">All Channels</option>
          <option value="whatsapp">WhatsApp</option>
          <option value="telegram">Telegram</option>
          <option value="email">Email</option>
        </select>
        <select value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}
          className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white">
          <option value="All">All Statuses</option>
          <option value="sent">Sent</option>
          <option value="delivered">Delivered</option>
          <option value="read">Read</option>
          <option value="failed">Failed</option>
          <option value="skipped">Skipped</option>
        </select>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-auto max-h-[65vh]">
          <table className="text-xs w-full">
            <thead className="sticky top-0 z-10 bg-white">
              <tr className="border-b border-gray-50">
                {["Channel", "Event", "To", "Status", "Booking", "When"].map(h => (
                  <th key={h} className="text-left text-[10.5px] font-semibold text-gray-400 uppercase tracking-wider px-3 py-2 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                <tr><td colSpan={6} className="text-center py-12 text-gray-400"><RefreshCw size={20} className="animate-spin mx-auto mb-2" /> Loading…</td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-10 text-gray-400 text-sm">No messages match this filter</td></tr>
              ) : logs.map(log => {
                const style = CHANNEL_STYLE[log.channel] || CHANNEL_STYLE.email;
                const { Icon } = style;
                return (
                  <tr key={log._id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1.5 text-[10.5px] font-semibold px-2 py-0.5 rounded-full ${style.color}`}>
                        <Icon size={11} /> {log.channel}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-700">{log.event}</td>
                    <td className="px-3 py-2 text-gray-500">{log.to || "—"}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[10.5px] font-medium px-2 py-0.5 rounded-full border ${STATUS_STYLE[log.status] || ""}`}>
                        {log.status}{log.error ? `: ${log.error}` : ""}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-mono text-gray-500 whitespace-nowrap">{log.booking?.bookingRef || "—"}</td>
                    <td className="px-3 py-2 text-gray-500 whitespace-nowrap">
                      {new Date(log.createdAt).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="text-sm px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">
              Previous
            </button>
            <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="text-sm px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
